import { useEffect, useRef, useState } from "react";
import WizardAppPreview from "../../../../assets/icons/WizardAppPreview";
import { ActionModuleSubtypesState } from "../../../../types/actionModule/editor/actionModuleEditorTypes";
import { SubtypeState } from "../../../../types/actionModule/editor/subtypeEditorTypes";
import { SubtypePreviewContainer } from "./SubtypePreview.styles";
import { Typography } from "@mui/material";
import {
    draggable,
    dropTargetForElements,
    type ElementDropTargetEventBasePayload,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
    attachClosestEdge,
    type Edge,
    extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { DraggableState, getSubtypeDragData } from "./dragAndDropHelpers";
import { Close } from "@mui/icons-material";
import { GeneralButton } from "../../../../common/ui/GeneralButton";
import { EmptySubtypesDropArea } from "./EmptySubtypesDropArea";

export type MoveSubtypeCallback = (idx1: number, idx2: number) => void;
export type InsertSubtypeCallback = (state: SubtypeState, index: number) => void;

interface Props {
    state: SubtypeState[];
    editorState: ActionModuleSubtypesState;
    onSelectSubtype: (subtype: SubtypeState) => void;
    onDeleteSubtype: (subtype: SubtypeState) => void;
}

export default function SubtypePreview(props: Props) {
    // The aspect ratio comes from the device frame size.
    const previewAspect = 334 / 682;

    return <div style={{
        minWidth: 0,
        height: "100%",
        flex: 1,
        position: "relative",
    }}>
        <div style={{
            height: "100%",
            width: "100%",
            position: "absolute",
        }}>
            <div style={{
                objectFit: "contain",
                aspectRatio: previewAspect,
                maxWidth: "100%",
                maxHeight: "100%",
            }}>
                <WizardAppPreview />
            </div>
        </div>
        <div style={{
            height: "100%",
            width: "100%",
            position: "absolute",
        }}>
            <div style={{
                objectFit: "contain",
                aspectRatio: previewAspect,
                maxWidth: "100%",
                maxHeight: "100%",
            }}>
                <div style={{
                    height: "100%",
                    paddingLeft: "5%",
                    paddingRight: "5%",
                    paddingTop: "12%",
                    paddingBottom: "4%",
                    boxSizing: "border-box"
                }}>
                    <div style={{
                        height: "100%",
                        overflowY: "scroll",
                        scrollbarWidth: "none",
                        display: "flex",
                        flexDirection: "column",
                    }}>
                        {props.state.map((s, i) => <SubtypePreviewItem
                            key={s.editorId}
                            index={i}
                            selected={props.editorState.selectedSubtype === s}
                            subtypeState={s}
                            onDelete={() => props.onDeleteSubtype(s)}
                            onClick={() => props.onSelectSubtype(s)}
                        />)}
                        {props.state.length === 0 && <EmptySubtypesDropArea />}
                    </div>
                </div>
            </div>
        </div>
    </div>
}

interface SubtypePreviewItemProps {
    subtypeState: SubtypeState;
    selected: boolean;
    index: number;
    onClick: () => void;
    onDelete: () => void;
}

function SubtypePreviewItem(props: SubtypePreviewItemProps) {
    const ref = useRef<HTMLDivElement>(null);

    const [draggableState, setDraggableState] = useState<DraggableState>({ type: "idle" })
    const [closestEdge, setClosestEdge] = useState<Edge | null>(null);

    const subtypeState = props.subtypeState;
    const index = props.index;
    const itemId = props.subtypeState.editorId;

    useEffect(() => {
        const element = ref.current!;
        const dragHandle = element;

        const data = getSubtypeDragData({
            subtype: subtypeState,
            index: index,
            itemId: subtypeState.editorId
        });

        function onChange({ source, self }: ElementDropTargetEventBasePayload) {
            const closestEdge = extractClosestEdge(self.data);

            const sourceIndex = source.data.index as number;

            const isItemBeforeSource = index === sourceIndex - 1;
            const isItemAfterSource = index === sourceIndex + 1;

            const isDropIndicatorHidden =
                (isItemBeforeSource && closestEdge === 'bottom') ||
                (isItemAfterSource && closestEdge === 'top');

            if (isDropIndicatorHidden) {
                setClosestEdge(null);
                return;
            }

            setClosestEdge(closestEdge);
        }

        return combine(
            draggable({
                element: dragHandle,
                getInitialData: () => data,
                onDragStart() {
                    setDraggableState({ type: "dragging" });
                },
                onDrop() {
                    setDraggableState({ type: "idle" })
                }
            }),
            dropTargetForElements({
                element,
                getData({ input }) {
                    return attachClosestEdge(data, {
                        element,
                        input,
                        allowedEdges: ['top', 'bottom'],
                    });
                },
                onDragEnter: onChange,
                onDrag: onChange,
                onDragLeave() {
                    setClosestEdge(null);
                },
                onDrop() {
                    setClosestEdge(null);
                },
            }),
        )
    }, [subtypeState, index, itemId]);

    const { name } = props.subtypeState.properties;

    const isDragged = draggableState.type === "dragging";

    return <div style={{
        position: "relative"
    }}>
        {closestEdge && <DropIndicator edge={closestEdge} />}
        {props.selected &&
            <div style={{
                position: "absolute",
                right: 0
            }}>
                <GeneralButton
                    appVariant="icon"
                    onClick={props.onDelete}
                >
                    <Close />
                </GeneralButton>
            </div>
        }
        <SubtypePreviewContainer
            selected={props.selected && !isDragged}
            onClick={() => props.onClick()}
            ref={ref}
            opacity={!isDragged ? 1 : 0.75}>
            <div style={{ pointerEvents: "none" }}>
                {name && <Typography variant="subtitle2">{name.toString()}</Typography>}

                {props.subtypeState.definition.editorPreview(props.subtypeState)}
            </div>
        </SubtypePreviewContainer>
    </div>
}
